/*
 * Copyright (c) 2024, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {ChakraProvider} from '@chakra-ui/react'

// Removes focus for non-keyboard interactions for the whole application
import 'focus-visible/dist/focus-visible'

import Fonts from '../../fonts'
import theme from '../../theme'
import {MultiSiteProvider} from '../../contexts'
import {useAppOrigin} from '../../hooks/use-app-origin'
import {resolveSiteFromUrl} from '../../utils/site-utils'
import {isServer, logQueryError, resolveLocaleFromUrl} from '../../utils/utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {createUrlTemplate} from '../../utils/url'
import createLogger from '@salesforce/pwa-kit-runtime/utils/logger-factory'
import {withLegacyGetProps} from '@salesforce/pwa-kit-react-sdk/ssr/universal/components/with-legacy-get-props'
import {withWaterfallReactQuery} from '../../hoc/with-waterfall-react-query'
import {ReactQueryDevtools} from '@tanstack/react-query-devtools'
import {useCorrelationId} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {DEFAULT_DNT_STATE} from '../../constants'
import {CommerceApiProvider} from '@salesforce/commerce-sdk-react'
import LegacyProviders from './legacy-providers'
import Chakra3Provider from '../../leap/theme'

/**
 * Use the AppConfig component to inject extra arguments into the getProps
 * methods for all Route Components in the app – typically you'd want to do this
 * to inject a connector instance that can be used in all Pages.
 *
 * You can also use the AppConfig to configure a state-management library such
 * as Redux, or Mobx, if you like.
 */
const AppConfig = ({children, locals = {}}) => {
    const {correlationId} = useCorrelationId()
    const headers = {
        'correlation-id': correlationId
    }

    const commerceApiConfig = locals.appConfig.commerceAPI

    const appOrigin = useAppOrigin()

    const passwordlessCallback = locals.appConfig.login?.passwordless?.callbackURI

    return (
        <CommerceApiProvider
            shortCode={commerceApiConfig.parameters.shortCode}
            clientId={commerceApiConfig.parameters.clientId}
            organizationId={commerceApiConfig.parameters.organizationId}
            siteId={locals.site?.id}
            locale={locals.locale?.id}
            currency={locals.locale?.preferredCurrency}
            redirectURI={`${appOrigin}/callback`}
            passwordlessLoginCallbackURI={passwordlessCallback}
            proxy={`${appOrigin}${commerceApiConfig.proxyPath}`}
            headers={headers}
            defaultDnt={DEFAULT_DNT_STATE}
            // Enable PWA Kit private client for SLAS integration
            enablePWAKitPrivateClient={true}
            logger={createLogger({packageName: 'commerce-sdk-react'})}
            fetchOptions={{
                // without on dev the basic auth credentials are sent for auth api calls
                // and breaks the login flow
                // @see https://github.com/SalesforceCommerceCloud/pwa-kit/pull/2190
                credentials: 'omit'
            }}
        >
            <MultiSiteProvider site={locals.site} locale={locals.locale} buildUrl={locals.buildUrl}>
                <Fonts />
                <ChakraProvider theme={theme}>
                    <Chakra3Provider>
                        <LegacyProviders>{children}</LegacyProviders>
                    </Chakra3Provider>
                </ChakraProvider>
                <ReactQueryDevtools />
            </MultiSiteProvider>
        </CommerceApiProvider>
    )
}

AppConfig.restore = (locals = {}) => {
    const path =
        typeof window === 'undefined'
            ? locals.originalUrl
            : `${window.location.pathname}${window.location.search}`

    const site = resolveSiteFromUrl(path)
    const locale = resolveLocaleFromUrl(path)

    const {app: appConfig} = getConfig()
    const apiConfig = {
        ...appConfig.commerceAPI,
        einsteinConfig: appConfig.einsteinAPI
    }

    apiConfig.parameters.siteId = site.id
    // this is a hack to make sure the siteId is correct for Einstein
    apiConfig.einsteinConfig.siteId = `${appConfig.einsteinAPI.siteId.split('-')[0]}-${site.id}`

    locals.buildUrl = createUrlTemplate(appConfig, site.alias || site.id, locale.id)
    locals.site = site
    locals.locale = locale
    locals.appConfig = appConfig
}

AppConfig.freeze = () => undefined

AppConfig.extraGetPropsArgs = (locals = {}) => {
    return {
        buildUrl: locals.buildUrl,
        site: locals.site,
        locale: locals.locale
    }
}

AppConfig.propTypes = {
    children: PropTypes.node,
    locals: PropTypes.object
}

// Recommended settings for PWA-Kit usages.
// NOTE: they will be applied on both server and client side.
// retry is always disabled on server side regardless of the value from the options
const options = {
    queryClientConfig: {
        // max depth of waterfall dependent queries to fetch on server side
        maxWaterfallDepth: 5,
        // warn when fetching all queries takes longer than this time on server side
        warnWaterfallTime: 2500,
        // warn when prepass takes longer than this time on server side
        warnPrepassTime: 250,
        // warn when fetching a single query takes longer than this time on server side
        warnQueryTime: 1000,
        defaultOptions: {
            queries: {
                retry: false,
                refetchOnWindowFocus: false,
                // cache for 2 seconds - this usually handles multiple requests
                // for the same resource during the same render
                staleTime: 2 * 1000,
                ...(isServer ? {retryOnMount: false} : {}),
                onError: (error) => {
                    logQueryError('Query error:', error)
                }
            },
            mutations: {
                retry: false,
                onError: (error) => {
                    logQueryError('Mutation error:', error)
                }
            }
        }
    },
    beforeHydrate: (data) => {
        const now = Date.now()

        // Helper to reset the data timestamp to time of app load.
        const updateQueryTimeStamp = ({state}) => {
            state.dataUpdatedAt = now
        }

        // Update serialized mutations and queries to ensure that the cached data is
        // considered fresh on first load.
        data?.mutations?.forEach(updateQueryTimeStamp)
        data?.queries?.forEach(updateQueryTimeStamp)

        return data
    }
}

export default withWaterfallReactQuery(withLegacyGetProps(AppConfig), options)
